/**
 * Imperative 2D-canvas rendering for the World viewer's map pane — the one
 * caller of `world-map-projection.ts`'s pure math. Kept out of
 * `WorldViewer.tsx` so the component stays JSX-and-wiring, mirroring how
 * `/fly`/`/assets`' 3D panes split their imperative three.js code out too.
 */

import { colorForObjectRef, hslToCss } from "../world/object-color.ts";
import { createMapProjection, type PanZoom, type Viewport, type WorldBoundsLike } from "./world-map-projection.ts";

export interface CanvasPlacement {
  readonly objectRef: string | null;
  readonly x: number;
  readonly y: number;
}

export interface CanvasAerodrome {
  readonly name: string;
  /** Already projected into the region's `x`/`y` space (`world/aerodrome-projection.ts`) — approximate, see that module's doc comment. */
  readonly x: number;
  readonly y: number;
}

export interface WorldCanvasRegion {
  readonly bounds: WorldBoundsLike;
  readonly placements: readonly CanvasPlacement[];
  readonly aerodromes: readonly CanvasAerodrome[];
}

const BACKGROUND = "#0a0d08";
const PLACEMENT_RADIUS_PX = 2.3;
const SELECTED_RADIUS_PX = 5.5;
const AERODROME_RADIUS_PX = 5;
const AERODROME_COLOR = "#f2c14e";
const LABEL_COLOR = "#f2e6c9";
const SELECTED_OUTLINE = "#ffffff";

function drawAerodromeMarker(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  // A dashed diamond — visually distinct from a placement dot, and the dash
  // reads as "approximate" (world/aerodrome-projection.ts's heuristic fit,
  // not a decoded position).
  ctx.save();
  ctx.setLineDash([2, 2]);
  ctx.strokeStyle = AERODROME_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x, y - AERODROME_RADIUS_PX);
  ctx.lineTo(x + AERODROME_RADIUS_PX, y);
  ctx.lineTo(x, y + AERODROME_RADIUS_PX);
  ctx.lineTo(x - AERODROME_RADIUS_PX, y);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

/** Draws `region` into `ctx` at `viewport` size under `panZoom`; `selectedIndex` (into `region.placements`) gets a highlighted ring. */
export function drawWorldMap(
  ctx: CanvasRenderingContext2D,
  region: WorldCanvasRegion,
  viewport: Viewport,
  panZoom: PanZoom,
  selectedIndex: number | undefined,
): void {
  const projection = createMapProjection(region.bounds, viewport, panZoom);

  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, viewport.width, viewport.height);

  region.placements.forEach((placement, index) => {
    const screen = projection.toScreen({ x: placement.x, y: placement.y });
    const selected = index === selectedIndex;
    const color = colorForObjectRef(placement.objectRef);
    ctx.beginPath();
    ctx.fillStyle = hslToCss(color);
    ctx.arc(screen.x, screen.y, selected ? SELECTED_RADIUS_PX : PLACEMENT_RADIUS_PX, 0, Math.PI * 2);
    ctx.fill();
    if (selected) {
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = SELECTED_OUTLINE;
      ctx.stroke();
    }
  });

  ctx.font = "10px monospace";
  ctx.textBaseline = "middle";
  for (const aerodrome of region.aerodromes) {
    const screen = projection.toScreen({ x: aerodrome.x, y: aerodrome.y });
    drawAerodromeMarker(ctx, screen.x, screen.y);
    ctx.fillStyle = LABEL_COLOR;
    ctx.fillText(aerodrome.name, screen.x + AERODROME_RADIUS_PX + 4, screen.y);
  }
}
